import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Stack,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add, Edit, Delete, Star, StarBorder } from '@mui/icons-material';
import * as documentNoteTemplateService from '../services/documentNoteTemplateService';
import { useTranslation } from 'react-i18next';
import { DocumentType, getDocumentTypeLabel, getDocumentTypeColor } from '../../../utils/documentNoteTemplateUtils';

const DocumentNoteTemplates = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [errors, setErrors] = useState({});

  const fetchTemplates = async () => {
    try {
      const response = await documentNoteTemplateService.getAllTemplates();
      setTemplates(response?.payload || []);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!templateName.trim()) {
      newErrors.templateName = 'Template name is required';
    } else if (templateName.length > 100) {
      newErrors.templateName = 'Template name must be less than 100 characters';
    }
    if (!noteContent.trim()) {
      newErrors.noteContent = 'Note content is required';
    } else if (noteContent.length > 1000) {
      newErrors.noteContent = 'Note content must be less than 1000 characters';
    }
    if (!documentType) {
      newErrors.documentType = 'Document type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const templateData = {
        TemplateName: templateName,
        NoteContent: noteContent,
        DocumentType: documentType
      };

      if (editTemplate) {
        templateData.Id = editTemplate.Id;
        await documentNoteTemplateService.updateTemplate(templateData);
      } else {
        await documentNoteTemplateService.createTemplate(templateData);
      }
      
      await fetchTemplates();
      handleClose();
    } catch (error) {
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await documentNoteTemplateService.deleteTemplate(id);
        await fetchTemplates();
      } catch (error) {
      }
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await documentNoteTemplateService.setAsDefault(id);
      await fetchTemplates();
    } catch (error) {
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditTemplate(null);
    setTemplateName('');
    setNoteContent('');
    setDocumentType('');
    setErrors({});
  };

  const handleEdit = (template) => {
    setEditTemplate(template);
    setTemplateName(template.TemplateName);
    setNoteContent(template.NoteContent);
    setDocumentType(template.DocumentType);
    setOpen(true);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Document Notes Templates</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setOpen(true);
            setEditTemplate(null);
            setTemplateName('');
            setNoteContent('');
            setDocumentType('');
          }}
        >
          Add Template
        </Button>
      </Stack>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Template Name</TableCell>
            <TableCell>Document Type</TableCell>
            <TableCell>Note Content</TableCell>
            <TableCell align="center">Default</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates?.map((template) => (
            <TableRow key={template.Id}>
              <TableCell>{template.TemplateName}</TableCell>
              <TableCell>
                <Chip 
                  label={getDocumentTypeLabel(template.DocumentType)} 
                  color={getDocumentTypeColor(template.DocumentType)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    maxWidth: 300, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {template.NoteContent}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <IconButton 
                  onClick={() => handleSetDefault(template.Id)}
                  color={template.IsDefault ? 'primary' : 'default'}
                  size="small"
                >
                  {template.IsDefault ? <Star /> : <StarBorder />}
                </IconButton>
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => handleEdit(template)}>
                  <Edit />
                </IconButton>
                <IconButton 
                  color="error" 
                  onClick={() => handleDelete(template.Id)}
                >
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {templates?.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="body2" color="text.secondary">
                  No templates found. Click "Add Template" to create one.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editTemplate ? 'Edit Template' : 'Add Template'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
              required
              error={!!errors.templateName}
              helperText={errors.templateName || `${templateName.length}/100 characters`}
              inputProps={{ maxLength: 100 }}
            />

            <FormControl fullWidth required error={!!errors.documentType}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                label="Document Type"
                disabled={!!editTemplate}
              >
                <MenuItem value={DocumentType.SaleTicket}>{getDocumentTypeLabel(DocumentType.SaleTicket)}</MenuItem>
                <MenuItem value={DocumentType.RepairTicket}>{getDocumentTypeLabel(DocumentType.RepairTicket)}</MenuItem>
                <MenuItem value={DocumentType.Invoice}>{getDocumentTypeLabel(DocumentType.Invoice)}</MenuItem>
                <MenuItem value={DocumentType.DeliveryNote}>{getDocumentTypeLabel(DocumentType.DeliveryNote)}</MenuItem>
                <MenuItem value={DocumentType.Offer}>{getDocumentTypeLabel(DocumentType.Offer)}</MenuItem>
                <MenuItem value={DocumentType.CreditNote}>{getDocumentTypeLabel(DocumentType.CreditNote)}</MenuItem>
                <MenuItem value={DocumentType.All}>{getDocumentTypeLabel(DocumentType.All)}</MenuItem>
              </Select>
              {errors.documentType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.documentType}
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Note Content"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              fullWidth
              required
              multiline
              rows={6}
              error={!!errors.noteContent}
              helperText={errors.noteContent || `${noteContent.length}/1000 characters`}
              inputProps={{ maxLength: 1000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentNoteTemplates;
